/**
 * Login component
 */

import FacebookIcon from '@mui/icons-material/Facebook';
import HomeIcon from '@mui/icons-material/Home';
import { CircularProgress, Icon } from '@mui/material';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import { INDEX, WebRoute } from 'config/fe-routes';
import React, { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { logInWithFacebook, logInWithGoogle } from 'utils/authentication';
import { redirectTo } from 'utils/route-helpers';

import LoginCheckoutService from '../../services/login-checkout.service';
import LoginTerms from './LoginTerms';

type LoginProps = {
  type?: 'page' | 'component';
  className?: string;
  excludeTerms?: boolean;
  loginRedirect?: WebRoute;
  hideLogo?: boolean;
  hideTexts?: boolean;
  hideHomeButton?: boolean;
  publishProductOnLogin?: boolean;
  isLoginLoading: boolean;
  storeData?: any;
};

const loginCheckoutService = new LoginCheckoutService();

const mapStateToProps = ({ userData }) => ({
  isLoginLoading: userData?.isLoading,
});

const Login = (props: LoginProps) => {
  const {
    type,
    className,
    excludeTerms,
    loginRedirect,
    hideLogo,
    hideTexts,
    hideHomeButton,
    publishProductOnLogin,
    isLoginLoading,
    storeData,
  } = props;
  const { t } = useTranslation();
  const loginRef = createRef<HTMLDivElement>();
  const termsRef = createRef<HTMLDivElement>();
  const [loginWith, setLoginWith] = useState<'fb' | 'google'>(null);

  const componentType = type || 'page';
  const introTexts: Array<string> = t('components.login.intro', {
    returnObjects: true,
  });

  // Add padding-bottom to scrollable content so fixed terms won't overlap buttons
  useEffect(() => {
    const loginEl = loginRef?.current;
    const termsEl = termsRef?.current;

    // For default type only
    if (componentType === 'page' && loginEl && termsEl) {
      loginEl.style.paddingBottom = termsEl.offsetHeight + 'px';
    }
  }, [loginRef]);

  return (
    <>
      <div className={classNames('login', `type-${componentType}`, className)} ref={loginRef}>
        {/* logo */}
        {!hideLogo ? <img className="login-logo" alt="logo" src="/logo.svg" /> : null}

        {/* intro text */}
        {!hideTexts ? (
          <div className="intro-texts">
            {(introTexts || []).map((introText, introTextIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <h6 key={'login-intro-text-' + introTextIndex}>{introText}</h6>
            ))}
          </div>
        ) : null}

        {/* login buttons */}
        <div className="login-buttons">
          {/* facebook */}
          <Button
            variant="text"
            onClick={() => {
              setLoginWith('fb');
              loginCheckoutService.setCheckoutData(storeData);
              logInWithFacebook(loginRedirect, { publishProductOnLogin });
            }}
            fullWidth={true}
            className="login-btn login-facebook-btn"
            startIcon={isLoginLoading && loginWith === 'fb' ? <CircularProgress size={24} className="load-icon" /> : <FacebookIcon />}
            disabled={isLoginLoading}
          >
            {t('components.login.facebook')}
          </Button>

          {/* google */}
          <Button
            variant="text"
            onClick={() => {
              setLoginWith('google');
              loginCheckoutService.setCheckoutData(storeData);
              logInWithGoogle(loginRedirect, { publishProductOnLogin });
            }}
            fullWidth={true}
            className="login-btn login-google-btn"
            startIcon={
              isLoginLoading && loginWith === 'google' ? (
                <CircularProgress size={24} className="load-icon" />
              ) : (
                <Icon baseClassName="fab fa-google" />
              )
            }
            disabled={isLoginLoading}
          >
            {t('components.login.google')}
          </Button>

          {/* home page */}
          {type === 'page' && !hideHomeButton ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => redirectTo(INDEX())}
              fullWidth={true}
              className="login-btn login-home-btn"
              startIcon={<HomeIcon />}
              disabled={isLoginLoading}
            >
              {t('components.login.home')}
            </Button>
          ) : null}
        </div>
      </div>

      {/* terms */}
      {!excludeTerms ? <LoginTerms ref={termsRef} type={type} /> : null}
    </>
  );
};

export default connect(mapStateToProps)(Login);
